import { CompanyDTO, CompanyReportingDataMappingDTO, CompanyRole } from '@iota/iota-management-portal-v4-client';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, createSelector, on } from '@ngrx/store';
import { isEmptyOrNull } from 'src/app/shared/utils';
import {
    AddUpdateRemoveCompanyAttributes,
    CompanyActionFailure,
    LoadCompanies,
    LoadCompaniesFailure,
    LoadCompaniesSuccess,
    LoadCompany,
    LoadCompanyFailure,
    LoadCompanyReportingDataWithMapping,
    LoadCompanyReportingDataWithMappingFailure,
    LoadCompanyReportingDataWithMappingSuccess,
    LoadCompanySuccess,
    LoadPlatformLicenseActivationsOfCompanyThatHaveNotBeenInvoiced,
    LoadPlatformLicenseActivationsOfCompanyThatHaveNotBeenInvoicedFailure,
    LoadPlatformLicenseActivationsOfCompanyThatHaveNotBeenInvoicedSuccess,
    LoadReportingDataMapping,
    LoadReportingDataMappingFailure,
    LoadReportingDataMappingSuccess,
    RemoveCompanyAddress,
    RemoveCompanyAttachment,
    RemoveCompanyBankDetails,
    RemoveCompanyNote,
    RemoveCompanyVendorId,
    RemovePlatformLicenseActivationFromSelectedCompany,
    RemovePlatformLicenseActivationFromSelectedCompanyFailure,
    UpsertCompany,
    UpsertCompanyAddress,
    UpsertCompanyAttachment,
    UpsertCompanyBankDetails,
    UpsertCompanyFailure,
    UpsertCompanyNote,
    UpsertCompanyPlatformLicenseActivation,
    UpsertCompanyPlatformLicenseActivationFailure,
    UpsertCompanySuccess,
    UpsertCompanyVendorId,
    UpsertReportingDataMappingFailure,
    UpsertReportingDataMappingSuccess
} from '../actions/company.actions';
import {
    CancelDeactivationSuccess,
    CreateDeactivationSuccess,
    RevertDeactivationSuccess,
    UpdateDeactivationSuccess
} from '../actions/company-deactivation.actions';
import { AppState } from '../store';
import { buildCompanyEntities } from '../entity-builder-utils';
import { CompanyDTO as INHCoreCompanyDTO, CompanyReportingDataView } from '@iota/inh-core-v1-management-client';
import moment from 'moment';

export interface CompanyState extends EntityState<INHCoreCompanyDTO> {
    loading: boolean;
    actionLoading: boolean;
    lastCreatedCompanyId: string;
    error: any;
    actionError: any;
    reportingDataMappingLoading: boolean;
    reportingDataMapping: CompanyReportingDataMappingDTO;
    reportingDataWithMappingLoading: boolean;
    reportingDataWithMapping: CompanyReportingDataView;
    current: CompanyDTO;

    licenseActivationIdsThatHaveNotBeenInvoiced: string[];
}

function selectCompanyId(a: INHCoreCompanyDTO): string {
    return a.id;
}

function sortCompanies(a: INHCoreCompanyDTO, b: INHCoreCompanyDTO): number {
    return moment(b.createdAt).toDate().getTime() - moment(a.createdAt).toDate().getTime();
}

export const companyAdapter: EntityAdapter<INHCoreCompanyDTO> = createEntityAdapter<INHCoreCompanyDTO>({
    selectId: selectCompanyId,
    sortComparer: sortCompanies
});

export const initialState: CompanyState = companyAdapter.getInitialState({
    loading: true,
    actionLoading: false,
    lastCreatedCompanyId: undefined,
    error: undefined,
    actionError: undefined,
    reportingDataMappingLoading: false,
    reportingDataMapping: undefined,
    reportingDataWithMappingLoading: false,
    reportingDataWithMapping: undefined,
    current: undefined,

    licenseActivationIdsThatHaveNotBeenInvoiced: []
});

export const companyReducer = createReducer(
    initialState,
    on(LoadCompanies, (state) => ({ ...state, loading: true })),
    on(LoadCompaniesSuccess, (state, { companies }) => {
        return companyAdapter.setAll(companies, { ...state, loading: false, actionLoading: false });
    }),
    on(LoadCompaniesFailure, (state, { error }) => ({ ...state, error, loading: false })),

    on(UpsertCompany, (state) => ({ ...state, actionLoading: true })),
    // Stays loading until the follow-up reload has replaced `current`.
    on(UpsertCompanySuccess, (state, { company, companyId }) =>
        companyAdapter.upsertOne(company, {
            ...state,
            error: null,
            lastCreatedCompanyId: companyId
        })
    ),
    on(UpsertCompanyFailure, (state) => ({ ...state, actionLoading: false })),

    on(AddUpdateRemoveCompanyAttributes, (state) => ({ ...state, actionLoading: true })),

    on(LoadCompany, (state) => ({ ...state, actionLoading: true })),
    on(LoadCompanySuccess, (state, { company }) => ({ ...state, actionLoading: false, current: company })),
    on(LoadCompanyFailure, (state, { error }) => ({ ...state, error, actionLoading: false, current: undefined })),

    on(CompanyActionFailure, (state, { error }) => ({ ...state, error, actionLoading: false })),

    /* CompanyAddress */
    on(UpsertCompanyAddress, (state) => ({ ...state, actionLoading: true })),
    on(RemoveCompanyAddress, (state) => ({ ...state, actionLoading: true })),

    /* CompanyNote */
    on(UpsertCompanyNote, (state) => ({ ...state, actionLoading: true })),
    on(RemoveCompanyNote, (state) => ({ ...state, actionLoading: true })),

    /* CompanyBankDetails */
    on(UpsertCompanyBankDetails, (state) => ({ ...state, actionLoading: true })),
    on(RemoveCompanyBankDetails, (state) => ({ ...state, actionLoading: true })),

    /* Attachments */
    on(UpsertCompanyAttachment, (state) => ({ ...state, actionLoading: true })),
    on(RemoveCompanyAttachment, (state) => ({ ...state, actionLoading: true })),

    /* CompanyVendorId */
    on(UpsertCompanyVendorId, (state) => ({ ...state, actionLoading: true })),
    on(RemoveCompanyVendorId, (state) => ({ ...state, actionLoading: true })),

    /* Company Reporting data mapping*/
    on(LoadReportingDataMapping, (state) => ({
        ...state,
        reportingDataMappingLoading: true
    })),
    on(LoadReportingDataMappingSuccess, (state, { data }) => ({
        ...state,
        reportingDataMapping: data,
        reportingDataMappingLoading: false
    })),
    on(LoadReportingDataMappingFailure, (state, { error }) => ({
        ...state,
        reportingDataMapping: undefined,
        error: error,
        reportingDataMappingLoading: false
    })),
    on(UpsertReportingDataMappingSuccess, (state, { data }) => ({
        ...state,
        reportingDataMapping: data,
        error: null
    })),
    on(UpsertReportingDataMappingFailure, (state: CompanyState, { error }) => ({
        ...state,
        error: error
    })),

    /* Company Reporting data with mapping*/
    on(LoadCompanyReportingDataWithMapping, (state) => ({
        ...state,
        reportingDataWithMappingLoading: true
    })),
    on(LoadCompanyReportingDataWithMappingSuccess, (state, { data }) => ({
        ...state,
        reportingDataWithMapping: data,
        reportingDataWithMappingLoading: false,
        error: null
    })),
    on(LoadCompanyReportingDataWithMappingFailure, (state, { error }) => ({
        ...state,
        reportingDataWithMapping: undefined,
        reportingDataWithMappingLoading: false,
        error: error
    })),

    /* Company Platform License Activation */
    on(
        UpsertCompanyPlatformLicenseActivation,
        RemovePlatformLicenseActivationFromSelectedCompany,
        LoadPlatformLicenseActivationsOfCompanyThatHaveNotBeenInvoiced,
        (state) => ({
            ...state,
            actionLoading: true,
            actionError: undefined
        })
    ),
    on(
        UpsertCompanyPlatformLicenseActivationFailure,
        RemovePlatformLicenseActivationFromSelectedCompanyFailure,
        LoadPlatformLicenseActivationsOfCompanyThatHaveNotBeenInvoicedFailure,
        (state, { error }) => ({
            ...state,
            actionLoading: false,
            actionError: error
        })
    ),
    on(LoadPlatformLicenseActivationsOfCompanyThatHaveNotBeenInvoicedSuccess, (state, { licenseActivationIdsThatHaveNotBeenInvoiced }) => ({
        ...state,
        actionLoading: false,
        actionError: undefined,
        licenseActivationIdsThatHaveNotBeenInvoiced
    })),

    on(CreateDeactivationSuccess, UpdateDeactivationSuccess, (state, { deactivation }) => {
        const companyUpdate = {
            scheduledOrExecutedCompanyDeactivation: deactivation,
            companyDeactivationDate: deactivation.companyDeactivationDate,
            isDeactivated: false
        };

        return companyAdapter.updateOne({ id: deactivation.companyId, changes: companyUpdate }, { ...state, actionLoading: false });
    }),

    on(CancelDeactivationSuccess, (state, { companyId }) => {
        const companyUpdate = {
            scheduledOrExecutedCompanyDeactivation: null,
            companyDeactivationDate: null,
            isDeactivated: false
        };

        return companyAdapter.updateOne({ id: companyId, changes: companyUpdate }, { ...state, actionLoading: false });
    }),

    on(RevertDeactivationSuccess, (state, { companyId }) => {
        const companyUpdate = {
            scheduledOrExecutedCompanyDeactivation: null,
            companyDeactivationDate: null,
            isDeactivated: false
        };

        return companyAdapter.updateOne({ id: companyId, changes: companyUpdate }, { ...state, actionLoading: false });
    })
);

const { selectAll } = companyAdapter.getSelectors();

export const companyState = (state: AppState) => state.companyState;
export const currentCompany = createSelector(companyState, (state) => state.current);
export const companiesLoading = createSelector(companyState, (state) => state.loading);
export const _companyDtos = createSelector(companyState, selectAll);
export const companies = createSelector(_companyDtos, (companies) => buildCompanyEntities(companies));
export const companyIdToNameMap = createSelector(
    companies,
    (allCompanies) => new Map(allCompanies?.map((company) => [company.companyId, company.name]))
);
export const companyIdToCompanyNumberMap = createSelector(
    companies,
    (allCompanies) => new Map(allCompanies?.map((company) => [company.companyId, company.companyNumber]))
);
export const companyIdToCompanyMap = createSelector(
    companies,
    (allCompanies) => new Map(allCompanies?.map((company) => [company.companyId, company]))
);
export const companiesByRole = (role: CompanyRole) =>
    createSelector(companies, (arr) => arr.filter((c) => !isEmptyOrNull(c.roles) && c.roles.includes(role)));
export const companiesByRoles = (roles: CompanyRole[]) =>
    createSelector(companies, (arr) => arr.filter((c) => !isEmptyOrNull(c.roles) && roles.some((role) => c.roles.includes(role))));
export const companyById = (companyId: string) =>
    createSelector(companies, (allCompanies) => allCompanies.find((c) => c.companyId === companyId));
export const companyActionLoading = createSelector(companyState, (state) => state.actionLoading);
export const companyActionError = createSelector(companyState, (state) => state.actionError);
export const lastCreatedCompanyId = createSelector(companyState, ({ lastCreatedCompanyId }) => lastCreatedCompanyId);

export const reportingDataMapping = createSelector(companyState, (state: CompanyState) => state.reportingDataMapping);

export const selectReportingDataWithMapping = createSelector(companyState, (state: CompanyState) => state.reportingDataWithMapping);

export const reportingDataMappingLoading = createSelector(companyState, (state: CompanyState) => state.reportingDataMappingLoading);

export const reportingDataWithMappingLoading = createSelector(companyState, (state: CompanyState) => state.reportingDataWithMappingLoading);

export const companiesWithRoleCustomer = createSelector(companies, (allCompanies) =>
    allCompanies.filter((company) => company.roles.includes(CompanyRole.CUSTOMER))
);

export const licenseActivationIdsOfCompanyThatHaveNotBeenInvoiced = createSelector(
    companyState,
    (state: CompanyState) => state.licenseActivationIdsThatHaveNotBeenInvoiced
);

export const inhCoreCompanyById = (companyId: string) =>
    createSelector(_companyDtos, (allCompanies) => allCompanies.find((c) => c.id === companyId));
